import "./OCRStatusList.scss";

import { useMemo } from "react";
import type {
  TextractRequest,
  TextractRequestStatus,
} from "services/rre-profile-ingestion-api";
import { useGetTextractRequestsByFileQuery } from "services/rre-profile-ingestion-api";
import { getTimeFromISOString } from "utils/date-utils";

type OCRStatusListProps = {
  fileId: number;
  // Lets a caller with its own dedicated status for one job type (e.g. MSADecisionOCRReview) keep this generic list from showing that job too, since two independently polled widgets could disagree about its status.
  excludeJobTypes?: string[];
};

const getStatusIcon = (status: TextractRequestStatus): string => {
  switch (status) {
    case "pending":
    case "in_progress":
      return "document_scanner";
    case "succeeded":
      return "check_circle";
    case "failed":
    case "partial_success":
      return "error";
    default:
      return "help";
  }
};

const getStatusText = (status: TextractRequestStatus): string => {
  switch (status) {
    case "pending":
      return "Queued for OCR processing...";
    case "in_progress":
      return "Processing OCR...";
    case "succeeded":
      return "OCR Complete";
    case "failed":
      return "OCR Failed";
    case "partial_success":
      return "OCR Partially Complete";
    default:
      return "Unknown Status";
  }
};

const extractFilename = (s3Path: string | null): string | null => {
  if (!s3Path) return null;
  const parts = s3Path.split("/");
  return parts[parts.length - 1] || null;
};

type OCRStatusItemProps = {
  request: TextractRequest;
};

const OCRStatusItem = ({ request }: OCRStatusItemProps) => {
  const isProcessing =
    request.status === "pending" || request.status === "in_progress";
  const isSuccess = request.status === "succeeded";
  const isError =
    request.status === "failed" || request.status === "partial_success";

  const filename = extractFilename(request.s3_filepath);

  return (
    <div
      className={`ocr-status-item ocr-status-item--${request.status}`}
      data-testid={`ocr-status-item-${request.id}`}
    >
      <div className="ocr-status-item__icon-wrapper">
        <span
          className={`material-icons ocr-status-item__icon ${isProcessing ? "ocr-status-item__icon--pulse" : ""}`}
        >
          {getStatusIcon(request.status)}
        </span>
      </div>
      <div className="ocr-status-item__content">
        <div className="ocr-status-item__status-text">
          {getStatusText(request.status)}
        </div>
        {filename && (
          <div className="ocr-status-item__filename" title={filename}>
            {filename}
          </div>
        )}
        {isError && request.error_message && (
          <div className="ocr-status-item__error">{request.error_message}</div>
        )}
      </div>
      <div className="ocr-status-item__timestamp">
        {getTimeFromISOString(
          isSuccess && request.completed_at
            ? request.completed_at
            : request.created,
          "dateTime",
        )}
      </div>
    </div>
  );
};

const isProcessingStatus = (status: TextractRequestStatus) =>
  status === "pending" || status === "in_progress";

export const OCRStatusList = ({
  fileId,
  excludeJobTypes,
}: OCRStatusListProps) => {
  // Poll unconditionally rather than bootstrapping from what is already there.
  // An upload creates its TextractRequest on a worker, so when the upload's
  // cache invalidation lands there may be nothing to see yet. Gating polling on
  // "is something already processing" meant that empty first response switched
  // polling off, and the widget stayed hidden for the whole job.
  const { data: textractRequests = [] } = useGetTextractRequestsByFileQuery(
    { fileId },
    { pollingInterval: 10000, skipPollingIfUnfocused: true },
  );

  const visibleRequests = useMemo(
    () =>
      excludeJobTypes?.length
        ? textractRequests.filter(
            (req) => !excludeJobTypes.includes(req.job_type),
          )
        : textractRequests,
    [textractRequests, excludeJobTypes],
  );

  const hasProcessingRequests = visibleRequests.some((req) =>
    isProcessingStatus(req.status),
  );

  // only show pending/in_progress requests, sorted by created date (newest first)
  const processingRequests = useMemo(() => {
    return visibleRequests
      .filter((req) => isProcessingStatus(req.status))
      .sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
      );
  }, [visibleRequests]);

  // only render when there are active processing requests
  if (processingRequests.length === 0) {
    return null;
  }

  return (
    <div className="ocr-status-list" data-testid="ocr-status-list">
      <div className="data-block">
        <div className="data-block-header">
          <span
            className={`material-icons ocr-status-list__header-icon ${hasProcessingRequests ? "ocr-status-list__header-icon--pulse" : ""}`}
          >
            document_scanner
          </span>
          OCR Processing Status
        </div>
        <div className="ocr-status-list__items">
          {processingRequests.map((request) => (
            <OCRStatusItem key={request.id} request={request} />
          ))}
        </div>
      </div>
    </div>
  );
};
